import { Button } from "@chakra-ui/react";
import InvestorAddModal from "features/investors/InvestorAddModal";
import { useInvestorMenu } from "features/investors/InvestorMenu";
import { useInvestorsTableDataRetrieveSuspense } from "gen/endpoints/investor-table-data/investor-table-data";
import { PermissionTypeEnum } from "gen/model/permissionTypeEnum";
import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { Restricted } from "shared/components/access/Restricted";
import PageBody from "shared/components/layout/PageBody";
import PageContainer from "shared/components/layout/PageContainer";
import PageContent from "shared/components/layout/PageContent";
import { TableBox } from "shared/components/table/box/TableBox";
import { TableBoxBody } from "shared/components/table/box/TableBoxBody";
import { TableBoxHeader } from "shared/components/table/box/TableBoxHeader";
import { TableBoxTitle } from "shared/components/table/box/TableBoxTitle";
import { DataTable } from "shared/components/table/data/DataTable";
import { DataTableBoxActions } from "shared/components/table/data/DataTableBoxActions";
import {
  GetLinkInfoForRowFn,
  RenderRowActionsFn,
} from "shared/components/table/data/DataTableTypes";
import useDocumentTitle from "shared/hooks/useDocumentTitle";
import { rootRoute } from "shared/navigation/Routes";
import { useHasPermission } from "shared/utils/PermissionUtils";
import { useBoolean } from "usehooks-ts";

export const InvestorListPage: React.FC = () => {
  const { t } = useTranslation(["common", "investor"]);
  const navigate = useNavigate();

  useDocumentTitle(t("common:investors"));

  const { data: tableData } = useInvestorsTableDataRetrieveSuspense();

  const hasConfigPermission = useHasPermission("config");

  const { getMenuItems } = useInvestorMenu();

  const {
    value: isInvestorAddModalOpen,
    setTrue: openInvestorAddModal,
    setFalse: closeInvestorAddModal,
  } = useBoolean();

  const addButton = (
    <Restricted permission={PermissionTypeEnum.config}>
      <Button size="xs" onClick={openInvestorAddModal}>
        {t("investor:add.button")}
      </Button>
    </Restricted>
  );

  const renderRowActions = useCallback<RenderRowActionsFn>(
    ({ row }) =>
      row.data?.metadata?.type === "id_row"
        ? {
            type: "menu",
            actions: getMenuItems({ investorId: row.data.metadata.id }),
          }
        : undefined,
    [getMenuItems],
  );

  return (
    <PageContainer>
      <PageContent isFullHeight={true} hasHeader={false}>
        <PageBody>
          <TableBox>
            <TableBoxHeader>
              <TableBoxTitle>{t("common:investors")}</TableBoxTitle>
              <DataTableBoxActions createAction={addButton} />
            </TableBoxHeader>
            <TableBoxBody>
              <DataTable
                data={tableData}
                fillWidth={true}
                getLinkInfoForRow={getLinkInfoForRow}
                disablePinnedHeaderColumns={true}
                emptyStateProps={{
                  title: t("investor:empty.title"),
                  description: t("investor:empty.description"),
                  children: addButton,
                }}
                renderRowActions={
                  hasConfigPermission ? renderRowActions : undefined
                }
              />
            </TableBoxBody>
          </TableBox>
          {isInvestorAddModalOpen && (
            <InvestorAddModal
              onSuccess={(data) => {
                navigate(
                  rootRoute({})
                    .investors({})
                    .investor({ investorId: data.id })
                    .activity({}).$,
                );
              }}
              onCloseComplete={closeInvestorAddModal}
            />
          )}
        </PageBody>
      </PageContent>
    </PageContainer>
  );
};

const getLinkInfoForRow: GetLinkInfoForRowFn = ({ row }) =>
  row.data?.metadata?.type === "id_row"
    ? {
        type: "generic",
        to: rootRoute({})
          .investors({})
          .investor({ investorId: row.data.metadata.id })
          .activity({}).$,
      }
    : undefined;
